<template>
  <div v-if="isLoggedIn" ref="target" :class="{ 'tw-mb-6': !modal }">
    <loading-input v-if="panelLoading" />

    <div v-else>
      <base-textarea-grow
        :model-value="comment"
        :placeholder="$t('add_comment')"
        :disabled="loading || disabled"
        :max="formValidation.comment.max"
        :errors="validationErrors.comment"
        :expanded="expanded"
        @input="(input) => updateField(input)"
        @click="focus"
        @focus="focus"
        @submit="submit"
      >
        <template #errors>
          <base-input-errors align="left" :errors="validationErrors.comment" />
        </template>
        <template #buttons>
          <base-button
            :small="modal"
            secondary
            full-width-at="sm"
            @click="cancel"
          >
            {{ $t('cancel') }}
          </base-button>

          <base-button
            primary
            :small="modal"
            :status="status"
            :input-loading="panelLoading"
            :disabled="loading || !dirty"
            full-width-at="sm"
            :errors="validationErrors"
            @click="submit"
          >
            {{ $t(submitText) }}
          </base-button>
        </template>
      </base-textarea-grow>
    </div>
  </div>

  <div v-else class="tw-space-y-2 tw-mb-6">
    <span>{{ $t('comment_login_prompt') }}</span>
    <login-button-bar
      @open:emailmodal="() => setGlobalModal({ modal: MODAL_LOGIN })"
    />
  </div>
</template>
<script>
import { discussionConfig as formValidation } from '@config/validation.js'
import { useValidate, useStatus, useAsync, addToast } from '@composables'
import { editDiscussionRequest, addDiscussionRequest } from '@services'
import { computed, nextTick, toRefs, inject, watch, ref } from 'vue'
import LoginButtonBar from '@components/Login/LoginButtonBar.vue'
import LoadingInput from '@components/Loading/LoadingInput.vue'
import { apiErrorsToArray, isNumber } from '@helpers/utils.js'
import { setGlobalModal } from '@composables/useModal.js'
import { MODAL_LOGIN } from '@config/globalModals.js'
import { DISCUSSION_TYPES } from '@config/options.js'
import { discussionStore, authStore } from '@stores'
import { onClickOutside } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

export default {
  components: {
    LoginButtonBar,
    LoadingInput,
  },
  props: {
    type: {
      validator: (val) => DISCUSSION_TYPES.includes(val),
      required: true,
    },
    replyId: {
      type: Number,
      default: 0,
    },
    resource: {
      type: Object,
      required: true,
    },
    gameNameId: {
      type: String,
      required: true,
    },
    panelLoading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    commentText: {
      type: String,
      default: '',
    },
    submitText: {
      type: String,
      default: 'post',
    },
    modal: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggle', 'submit'],
  setup(props, { emit }) {
    const { gameNameId, replyId, type, resource, commentText, modal } =
      toRefs(props)

    const resourceNameId = resource.value.name_id

    const comment = ref('')
    const target = ref()
    const expanded = ref(false)

    const { isLoggedIn, user } = authStore()
    const { t } = useI18n()

    const editId = inject('editId')
    const editing = computed(() => editId.value === replyId.value)

    const dirty = computed(
      () => !editing.value || comment.value !== commentText.value
    )

    const {
      errors: validationErrors,
      hasErrors,
      validate,
      resetErrors,
    } = useValidate(formValidation.validation)

    const { addDiscussion, updateDiscussion } = discussionStore(type.value)

    const { loading, error, errorRef, data, run } = useAsync(() =>
      editing.value
        ? editDiscussionRequest(
            gameNameId.value,
            resourceNameId,
            type.value,
            replyId.value,
            comment.value
          )
        : addDiscussionRequest({
            gameNameId: gameNameId.value,
            resourceType: type.value,
            resourceNameId,
            comment: comment.value,
            replyId: replyId.value,
          })
    )

    const { status } = useStatus({
      loading,
      error,
    })

    watch(
      [editId, replyId],
      ([_editId, _replyId]) => {
        if (isNumber(_editId) && isNumber(_replyId)) {
          if (_editId === _replyId) {
            comment.value = commentText.value
            focus()
          } else {
            cancel()
          }
        }
      },
      {
        immediate: true,
      }
    )

    function updateField(input) {
      if (input.length > 0) {
        validate('comment', input)
      }
      comment.value = input
    }

    function focus() {
      expanded.value = true

      nextTick(() => {
        target.value.querySelector('textarea')?.focus()
      })
    }

    function blur(val) {
      if (comment.value === '' || val === 'btn') {
        expanded.value = false
        resetErrors()
        emit('toggle')
      }
    }

    function cancel() {
      blur('btn')
      comment.value = ''
    }

    onClickOutside(target, blur)

    async function submit() {
      if (loading.value) return

      validate('comment', comment.value)

      if (hasErrors()) return

      if (
        user.value.info &&
        (!user.value.info.email || !user.value.info.status)
      ) {
        validationErrors.comment = [
          "You must verify your account before you're able to post comments",
        ]
        return
      }

      await run()

      if (!error.value) {
        if (editing.value) {
          updateDiscussion(gameNameId.value, resourceNameId, replyId.value, {
            content: data.value.content,
          })
        } else if (!modal.value) {
          addDiscussion(gameNameId.value, resourceNameId, data.value)
        }

        blur('btn')

        if (!editing.value) {
          comment.value = ''
        }

        emit('submit')
      } else {
        addToast({
          title: t(editing.value ? 'comment_error_edit' : 'comment_error_add'),
          isError: true,
          errorRef: errorRef.value,
          text: apiErrorsToArray(error.value).join(' '),
        })
      }
    }

    return {
      validationErrors,
      formValidation,
      setGlobalModal,
      MODAL_LOGIN,
      updateField,
      isLoggedIn,
      expanded,
      loading,
      comment,
      status,
      cancel,
      submit,
      target,
      dirty,
      focus,
      blur,
    }
  },
}
</script>
